import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  HomeModernIcon,
  SparklesIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

import { ManagedProperty } from '../property/property-card/PropertyCardTypes';

type ManagedPropertiesStatsProps = {
  activeProperties: ManagedProperty[];
  archivedProperties: ManagedProperty[];
  onManagedUnitsClick?: () => void;
};

const ManagedPropertiesStats: React.FC<ManagedPropertiesStatsProps> = ({
  activeProperties,
  archivedProperties,
  onManagedUnitsClick
}) => {
  const navigate = useNavigate();

  // Helper to format numbers (remove .0 if whole number)
  const formatAvg = (num: number) => {
    const formatted = num.toFixed(1);
    return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
  };

  const stats = useMemo(() => {
    const totalUnits = activeProperties.length;

    const totalBedrooms = activeProperties.reduce(
      (sum, property) => sum + (property.bedrooms || 0),
      0
    );

    const totalBathrooms = activeProperties.reduce(
      (sum, property) => sum + (property.bathrooms || 0),
      0
    );

    const avgBedsRaw = totalUnits ? totalBedrooms / totalUnits : 0;
    const avgBathsRaw = totalUnits ? totalBathrooms / totalUnits : 0;

    return [
      {
        title: 'Managed Units',
        value: totalUnits,
        subtext: `${archivedProperties.length} archived`,
        icon: BuildingOffice2Icon,
        accent: 'from-blue-500 to-indigo-500',
        isClickable: true // Explicit flag for UI logic
      },
      {
        title: 'Avg. Bedrooms',
        value: formatAvg(avgBedsRaw),
        subtext: `Total: ${totalBedrooms}`, // Clearer label
        icon: HomeModernIcon,
        accent: 'from-emerald-500 to-green-500',
        isClickable: false
      },
      {
        title: 'Avg. Bathrooms',
        value: formatAvg(avgBathsRaw),
        subtext: `Total: ${totalBathrooms}`, // Clearer label
        icon: SparklesIcon,
        accent: 'from-amber-500 to-orange-500',
        isClickable: false
      }
    ];
  }, [activeProperties, archivedProperties]);

  // Do not render stats when there are no active properties
  if (!activeProperties || activeProperties.length === 0) {
    return null;
  }

  const handleCardClick = (isClickable: boolean) => {
    if (!isClickable) return;

    if (onManagedUnitsClick) {
      onManagedUnitsClick();
    } else {
      navigate('/landlord-toolkit/managed-properties');
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {stats.map((stat) => (
        <div
          key={stat.title}
          onClick={() => handleCardClick(stat.isClickable)}
          // Dynamic classes based on isClickable
          className={`
            relative flex flex-col justify-between rounded-2xl border border-white/60 bg-white/80 p-6 shadow-lg shadow-blue-50/50 backdrop-blur transition-all duration-300
            ${
              stat.isClickable
                ? 'cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-100'
                : 'cursor-default'
            }
          `}
        >
          {/* Top Row: Icon + Interaction Cue */}
          <div className="flex w-full items-start justify-between">
            <div
              className={`mb-4 inline-flex rounded-2xl bg-gradient-to-br ${stat.accent} p-3 text-white shadow-sm`}
            >
              <stat.icon className="h-6 w-6" />
            </div>

            {/* Visual Cue: Only show Chevron if clickable */}
            {stat.isClickable && (
              <div className="rounded-full bg-gray-50 p-1 text-gray-400 group-hover:text-blue-500">
                <ChevronRightIcon className="h-5 w-5" />
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
              {stat.title}
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>

              {/* Conditional styling for subtext (dim if 0) */}
              <span
                className={`text-sm ${stat.subtext.startsWith('0') ? 'text-gray-300' : 'text-gray-500'}`}
              >
                {stat.subtext}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ManagedPropertiesStats;
